import * as Sentry from "@sentry/react";
import * as T from "@shared/types";
import { TFunction } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  CurrencyObject,
  DEFAULT_USER_LOCALE,
  INTERNAL_CUSTOMER_ACCOUNT_DISPLAY_NAME,
  REGIONS_WITH_CURRENCY,
  REGIONS_WITH_LOCALES,
} from '@client-shared/lib/constants';
import { useSFDCAuthState } from '../../providers/auth/useSFDCAuthState';
import * as api from '@client-shared/services/api';
import { handleDisplayErrorData } from '../../services/api/utilities/handleDisplayErrorData';
import {
  initialState,
  reducer,
  setCurrencyObject as setCurrencyObjectAction,
  setCurrentLocale,
  setCustomerAccountCarrierDetails,
  setError,
  setInitialized,
  setLoading,
  setRegionLocales,
  setVendor,
} from "./actions";
import { UseVendorUtils, VendorActions, VendorContextState, vendorLocalStorageKeys } from "./types";
const defaultContextState: VendorContextState = [initialState, () => undefined];

export const VendorContext = React.createContext(defaultContextState);

type Props = {
  children: React.ReactNode;
};

export const VendorProvider: React.FC<Props> = ({ children }: Props) => {
  const [authState] = useSFDCAuthState();
  const [vendorState, vendorDispatch] = React.useReducer(reducer, initialState);
  const { i18n } = useTranslation();
  const translations = React.useMemo(() => i18n.t, [i18n.t]);

  React.useEffect(() => {
    if (vendorState.currentVendor) return;
    const vendorObject = localStorage.getItem(vendorLocalStorageKeys.vendor);
    const currentLocaleValue = localStorage.getItem(vendorLocalStorageKeys.currentLocale);

    if (!vendorObject) return;

    const vendor = JSON.parse(vendorObject) as T.Vendor;
    const locale = currentLocaleValue as T.Locale;

    vendorDispatch(setVendor(vendor));
    if (currentLocaleValue) {
      vendorDispatch(setCurrentLocale(locale));
    }
    vendorDispatch(setInitialized(true));
  }, [vendorState.currentVendor]);

  React.useEffect(() => {
    if (!vendorState.initialized) return;
    if (!vendorState.currentVendor) return;
    Sentry.setUser({
      email: vendorState.currentVendor?.email ?? undefined,
      id: vendorState.currentVendor?.vendorKey,
      username: vendorState.currentVendor?.vendorName ?? undefined,
    });
  }, [vendorState.initialized, vendorState.currentVendor]);

  React.useEffect(() => {
    if (!vendorState.initialized || vendorState.regionLocales || !vendorState.currentVendor || !vendorState.currentLocale) return;
    vendorDispatch(setRegionLocales(getRegionLocales(vendorState.currentVendor.region)));
  }, [vendorState.currentLocale, vendorState.currentVendor, vendorState.initialized, vendorState.regionLocales]);

  React.useEffect(() => {
    if (!vendorState.initialized || !vendorState?.currentVendor?.region || vendorState?.currencyObject) return;
    const currencyObject: CurrencyObject = setCurrencyObjectFromVendor(vendorState.currentVendor);
    vendorDispatch(setCurrencyObjectAction(currencyObject));
  }, [
    vendorState.initialized,
    vendorState.currentVendor,
    vendorState.currentLocale,
    vendorState.currentVendor?.region,
    vendorState?.currencyObject,
  ]);

  React.useEffect(() => {
    if (
      !vendorState.initialized ||
      vendorState.currentLocale ||
      !vendorState.currentVendor ||
      !authState.sfdcAuthToken ||
      !vendorState.currencyObject
    ) {
      return;
    }
    fetchLocale(vendorState.currentVendor, authState.sfdcAuthToken, vendorDispatch, translations);
  }, [
    authState.sfdcAuthToken,
    translations,
    vendorState.currentVendor,
    vendorState.initialized,
    vendorState.currentLocale,
    vendorState.currencyObject,
  ]);

  React.useEffect(() => {
    if (!vendorState.initialized || !vendorState.currentLocale || !vendorState.currentVendor) return;
    i18n.changeLanguage(vendorState.currentLocale);
  }, [i18n, vendorState, vendorState.currentVendor, vendorState.initialized, vendorState.currentLocale]);

  React.useEffect(() => {
    if (
      !vendorState.initialized ||
      vendorState.customerAccountCarrierDetails ||
      !vendorState.currentVendor ||
      !authState.sfdcAuthToken ||
      !vendorState.currencyObject ||
      !vendorState.currentLocale ||
      !vendorState.currentVendor.region ||
      !vendorState.regionLocales
    ) {
      return;
    }
    fetchCustomerAccountCarrierDetails(vendorDispatch, translations);
  }, [
    authState.sfdcAuthToken,
    vendorState.currentVendor,
    vendorState.initialized,
    vendorState.customerAccountCarrierDetails,
    vendorState.currencyObject,
    vendorState.currentLocale,
    vendorState.regionLocales,
    translations,
  ]);

  const updateProperties = React.useCallback(
    async (properties: T.VendorProperties) => {
      if (!vendorState.currentVendor) return;
      vendorDispatch(setLoading(true));
      const isSuccess = await updateVendorProperties(properties, vendorState.currentVendor, vendorDispatch, i18n.t);
      vendorDispatch(setLoading(false));
      return isSuccess;
    },
    [i18n.t, vendorState.currentVendor, vendorDispatch]
  );

  const useVendorMethods: UseVendorUtils = React.useMemo(() => {
    return {
      updateProperties,
      setError: (error: Error) => vendorDispatch(setError(error)),
    };
  }, [updateProperties]);

  const contextValue: VendorContextState = React.useMemo(
    () => [vendorState, vendorDispatch, useVendorMethods],
    [vendorState, useVendorMethods]
  );
  return <VendorContext.Provider value={contextValue}>{children}</VendorContext.Provider>;
};

// utils
export const setCurrencyObjectFromVendor = (currentVendor: T.Vendor) => {
  return REGIONS_WITH_CURRENCY[currentVendor.region as keyof typeof REGIONS_WITH_CURRENCY];
};

export const verifyVendorLocale = (vendor: T.Vendor, locale?: T.Locale): T.Locale => {
  if (!vendor.region) return DEFAULT_USER_LOCALE;
  const defaultLocale =
    REGIONS_WITH_LOCALES[vendor.region as keyof typeof REGIONS_WITH_LOCALES].find((obj) => obj.isDefault)?.locale ?? DEFAULT_USER_LOCALE;
  if (!locale) return defaultLocale;
  if (REGIONS_WITH_LOCALES[vendor.region as keyof typeof REGIONS_WITH_LOCALES].find((obj) => obj.locale === locale)) return locale;
  return defaultLocale;
};

export const getRegionLocales = (region: T.Vendor["region"]): T.Locale[] => {
  return REGIONS_WITH_LOCALES[region as keyof typeof REGIONS_WITH_LOCALES].map(
    (regionLocaleObject: T.RegionLocaleObject) => regionLocaleObject.locale
  );
};

export const getEnabledVendorCustomerAccountCarrierDetails = (customerAccountCarrierDetails: T.CarrierCustomerAccountBinding[]) => {
  if (!customerAccountCarrierDetails) return [];
  const groupedCustomerAccounts = customerAccountCarrierDetails.reduce<T.CarrierCustomerAccountBinding[]>((acc, detail) => {
    const { zenkraftShippingAccount, labelPrintEnabled, isManifestEnabled, accountType } = detail;

    const existingGroup = acc.find(
      (group) =>
        group.zenkraftShippingAccount === zenkraftShippingAccount &&
        group.labelPrintEnabled === labelPrintEnabled &&
        group.isManifestEnabled === isManifestEnabled
    );

    if (existingGroup) {
      existingGroup.customerAccountName = accountType;
      existingGroup.customerAccountNumber += `,${detail.customerAccountNumber}`;
      existingGroup.id = btoa(existingGroup.customerAccountNumber);
      existingGroup.groupedCustomerAccounts?.push({
        customerAccountDisplayName: detail.customerAccountDisplayName ?? detail.customerAccountName ?? accountType,
        customerAccountNumber: detail.customerAccountNumber,
        isBrevilleOwnedCarrierAccount: detail.isBrevilleOwnedCarrierAccount,
      });
      const isBrevilleOwnedCarrierAcccountGroup = existingGroup.groupedCustomerAccounts?.some(
        (groupedAccount) => groupedAccount.isBrevilleOwnedCarrierAccount
      );

      const baseDisplayName = isBrevilleOwnedCarrierAcccountGroup ? INTERNAL_CUSTOMER_ACCOUNT_DISPLAY_NAME : `${detail.accountType}`;

      const existingNames = new Set(acc.filter((group) => group !== existingGroup).map((group) => group.customerAccountDisplayName));

      let uniqueName = baseDisplayName;
      let suffix = 0;

      while (existingNames.has(uniqueName)) {
        suffix++;
        uniqueName = `${baseDisplayName} - ${suffix}`;
      }

      existingGroup.customerAccountDisplayName = uniqueName;
    } else {
      const customerAccountDisplayName =
        detail.accountType === "Beanz" && detail.isBrevilleOwnedCarrierAccount
          ? INTERNAL_CUSTOMER_ACCOUNT_DISPLAY_NAME
          : (detail.customerAccountDisplayName ?? detail.customerAccountName ?? detail.accountType);
      acc.push({
        ...detail,
        customerAccountDisplayName,
        groupedCustomerAccounts: [
          {
            customerAccountDisplayName: detail.customerAccountDisplayName ?? detail.customerAccountName ?? accountType,
            customerAccountNumber: detail.customerAccountNumber,
            isBrevilleOwnedCarrierAccount: detail.isBrevilleOwnedCarrierAccount,
          },
        ],
        id: btoa(detail.customerAccountNumber),
      });
    }

    return acc;
  }, []);
  groupedCustomerAccounts.sort((a, b) => {
    const aIsBrevilleOwned = a.groupedCustomerAccounts?.some((account) => account.isBrevilleOwnedCarrierAccount) ?? false;
    const bIsBrevilleOwned = b.groupedCustomerAccounts?.some((account) => account.isBrevilleOwnedCarrierAccount) ?? false;
    return bIsBrevilleOwned ? 1 : aIsBrevilleOwned ? -1 : 0;
  });
  return groupedCustomerAccounts;
};

//sideEffects
const fetchLocale = async (
  currentVendor: T.Vendor,
  sfdcAuthToken: string,
  dispatch: (action: VendorActions) => void,
  t: TFunction<"translation", undefined>
) => {
  try {
    const userLocale = await api.getUserLocale(sfdcAuthToken);
    dispatch(setCurrentLocale(verifyVendorLocale(currentVendor, userLocale)));
  } catch (e) {
    console.error(e);
    handleDisplayErrorData(t("apiErrorMessages.fetchUserLocale.message"), t("apiErrorMessages.fetchUserLocale.title"), e as Error);
  }
};

const fetchCustomerAccountCarrierDetails = async (dispatch: (action: VendorActions) => void, t: TFunction<"translation", undefined>) => {
  try {
    const customerAccountCarrierDetails = await api.getCustomerAccountCarrierDetails();
    dispatch(setCustomerAccountCarrierDetails(getEnabledVendorCustomerAccountCarrierDetails(customerAccountCarrierDetails)));
  } catch (e) {
    console.error(e);
    handleDisplayErrorData(
      t("apiErrorMessages.getCustomerAccountCarrierDetails.message"),
      t("apiErrorMessages.getCustomerAccountCarrierDetails.title"),
      e as Error
    );
  }
};

const updateVendorProperties = async (
  properties: T.VendorProperties,
  currentVendor: T.Vendor,
  dispatch: (action: VendorActions) => void,
  t: TFunction<"translation", undefined>
) => {
  try {
    await api.setVendorProperties(properties);
    dispatch(setError(undefined));
    // toast.dark(t(message));
    dispatch(
      setVendor({
        ...currentVendor,
        properties: {
          ...currentVendor.properties,
          ...properties,
        },
      })
    );
    return true;
  } catch (e) {
    console.error(e);
    dispatch(setError(e as Error));
    handleDisplayErrorData("message", t("apiErrorMessages.updateVendorProperties.title"), e as Error);
    return false;
  }
};
